import { FC, useMemo, useState } from 'react';

import { getShopOrderCsMemoLog } from '@shared/api/shopOrderCsMemo/shopOrderCsMemo.controller';
import { useShopOrderCsMemoLogQuery } from '@shared/api/shopOrderCsMemo/shopOrderCsMemo.hook';
import {
  ShopOrderCsMemoLogItem,
  ShopOrderCsMemoLogParams,
} from '@shared/api/shopOrderCsMemo/shopOrderCsMemo.interface';
import { CsMemoModalBase } from '@shared/components/csMemo';
import { Loading } from '@shared/components/Loading';
import { toastError, toastSuccess } from '@shared/plugIn/toast';
import { useAuth } from '@shared/state/hooks/useAuth';
import { bareReceiverName } from '@shared/utils/receiverName.util';

import { shopOrderCsMemoAdapter } from '../components/csMemo';
import { useSellerTopNavHook } from '../hooks/useSellerTopNavHook';
import { exportShopCsMemoLogExcel } from '../utils/shopCsMemoLogExcel';

const pad = (n: number) => String(n).padStart(2, '0');
const toDateInput = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateInput(d);
};

const fmtDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${String(d.getFullYear()).slice(2)}.${pad(d.getMonth() + 1)}.${pad(
    d.getDate(),
  )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EXPORT_PAGE_SIZE = 200;
const EXPORT_MAX_PAGES = 10; // 최대 2000건

/**
 * 화환 CS 응대 로그 — 남긴 응대 메모를 기간별로 훑는 표.
 * 셀러는 자기 매장 메모만(서버가 거른다), 관리자 전용 메모도 서버가 셀러 응답에서 뺀다.
 */
export const SellerCsMemoLogPage: FC = function SellerCsMemoLogPage() {
  useSellerTopNavHook('CS 응대 로그');
  const { isAdmin } = useAuth();
  const admin = isAdmin();

  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(toDateInput(new Date()));
  const [adminOnly, setAdminOnly] = useState(false);
  const [limit, setLimit] = useState(50);
  // 입력 중인 검색어와 실제 조회에 쓰는 검색어를 분리 (타이핑마다 조회하지 않는다)
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [modalItem, setModalItem] = useState<ShopOrderCsMemoLogItem | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const params = useMemo<ShopOrderCsMemoLogParams>(
    () => ({
      from,
      to,
      ...(query ? { q: query } : {}),
      ...(admin && adminOnly ? { adminOnly: true } : {}),
      page,
      limit,
    }),
    [from, to, query, admin, adminOnly, page, limit],
  );

  const { data, isLoading, isFetching } = useShopOrderCsMemoLogQuery(params);
  const unavailable = data === null;
  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  // 작성자 분포는 "지금 보고 있는 페이지" 기준
  const writerCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of items) map.set(item.byName, (map.get(item.byName) ?? 0) + 1);
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [items]);

  const applyRange = (fromDate: string) => {
    setFrom(fromDate);
    setTo(toDateInput(new Date()));
    setPage(1);
  };

  const runSearch = () => {
    setQuery(searchInput.trim());
    setPage(1);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const all: ShopOrderCsMemoLogItem[] = [];
      for (let p = 1; p <= EXPORT_MAX_PAGES; p += 1) {
        const res = await getShopOrderCsMemoLog({
          ...params,
          page: p,
          limit: EXPORT_PAGE_SIZE,
        });
        all.push(...res.items);
        if (all.length >= res.total || res.items.length < EXPORT_PAGE_SIZE) break;
      }
      if (all.length === 0) {
        toastError('내보낼 메모가 없습니다.');
        return;
      }
      exportShopCsMemoLogExcel(all, { from, to });
      if (all.length < total) {
        toastSuccess(
          `${all.length}건 내보냈습니다. (한 번에 최대 ${
            EXPORT_PAGE_SIZE * EXPORT_MAX_PAGES
          }건 — 기간을 나눠 받아주세요)`,
        );
      }
    } catch {
      toastError('엑셀 내보내기에 실패했습니다.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 pb-8 sm:px-6 lg:px-8">
      {/* 필터 */}
      <div className="mt-6 space-y-2 rounded-lg bg-white p-4 shadow">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border px-3 py-2 text-sm"
          />
          <span className="text-gray-400">~</span>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border px-3 py-2 text-sm"
          />
          <div className="flex items-center gap-1">
            {[
              { label: '오늘', days: 0 },
              { label: '7일', days: 7 },
              { label: '30일', days: 30 },
              { label: '90일', days: 90 },
            ].map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => applyRange(daysAgo(preset.days))}
                className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50"
              >
                {preset.label}
              </button>
            ))}
          </div>

          {admin && (
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={adminOnly}
                onChange={(e) => {
                  setAdminOnly(e.target.checked);
                  setPage(1);
                }}
                className="h-3.5 w-3.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              관리자 전용만
            </label>
          )}

          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runSearch();
            }}
            placeholder="내용, 작성자, 보내는 분, 받는 분"
            className="min-w-[200px] flex-1 rounded-lg border px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={runSearch}
            className="rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-800"
          >
            검색
          </button>
          {query && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setQuery('');
                setPage(1);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              초기화
            </button>
          )}
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={isExporting || unavailable || total === 0}
            className="rounded-lg border border-emerald-300 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            title="현재 조회 조건 그대로 엑셀로 내보내기"
          >
            {isExporting ? '내보내는 중...' : '엑셀'}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span>
            총 <span className="font-bold text-gray-800">{total}</span>건
          </span>
          <span className="text-gray-400">{admin ? '전 매장' : '내 매장'}</span>
          {isFetching && <span className="text-cyan-700">불러오는 중...</span>}
          {writerCounts.length > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-gray-400">이 페이지 작성자</span>
              {writerCounts.map(([name, count]) => (
                <span
                  key={name}
                  className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600"
                >
                  {name} {count}
                </span>
              ))}
            </>
          )}
        </div>
      </div>

      {/* 목록 */}
      <div className="mt-4">
        {isLoading ? (
          <div className="center-box py-20">
            <Loading />
          </div>
        ) : unavailable ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-sm text-gray-400">
            CS 응대 로그가 아직 서버에 반영되지 않았습니다.
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-sm text-gray-400">
            조회 기간에 남긴 메모가 없습니다.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 font-medium">작성일시</th>
                  <th className="whitespace-nowrap px-4 py-3 font-medium">작성자</th>
                  <th className="whitespace-nowrap px-4 py-3 font-medium">주문</th>
                  <th className="px-4 py-3 font-medium">내용</th>
                  <th className="whitespace-nowrap px-4 py-3 font-medium">메모장</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => {
                  const badge = shopOrderCsMemoAdapter.roleBadge(item.byRole);
                  return (
                    <tr
                      key={item._id}
                      className={item.adminOnly ? 'bg-amber-50/50' : 'hover:bg-gray-50'}
                    >
                      <td className="whitespace-nowrap px-4 py-2.5 text-xs text-gray-500">
                        {fmtDateTime(item.createdAt)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <span className="text-xs font-medium text-gray-700">
                          {item.byName}
                        </span>
                        <span
                          className={`ml-1 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                        {item.adminOnly && (
                          <span className="ml-1 inline-flex rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-amber-800">
                            관리자 전용
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-xs">
                        {item.order ? (
                          <>
                            <div className="text-gray-700">
                              {item.order.senderName || '-'}
                              <span className="mx-1 text-gray-300">→</span>
                              {bareReceiverName(item.order.receiverName) || '-'}
                            </div>
                            {item.order.itemName && (
                              <div className="text-gray-500">{item.order.itemName}</div>
                            )}
                            <div className="text-[11px] text-gray-400">
                              {[item.order.paymentStatus, item.order.deliveryStatus]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                          </>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="max-w-[520px] px-4 py-2.5">
                        <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-800">
                          {item.body}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        {item.shopOrder ? (
                          <button
                            type="button"
                            onClick={() => setModalItem(item)}
                            className="rounded-md border border-cyan-200 px-2 py-1 text-[11px] font-medium text-cyan-700 hover:bg-cyan-50"
                            title="이 주문의 메모장 열기 (이어서 남기기)"
                          >
                            열기
                          </button>
                        ) : (
                          <span className="text-[11px] text-gray-300">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 페이지 이동 */}
        {!unavailable && total > 0 && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>페이지당</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="rounded-lg border px-2 py-1 text-xs"
              >
                {[50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}건
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                이전
              </button>
              <span className="text-xs text-gray-500">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>

      {modalItem?.shopOrder && (
        <CsMemoModalBase
          adapter={shopOrderCsMemoAdapter}
          title="주문 CS 응대 메모"
          subtitle={
            modalItem.order
              ? `보내는 분 ${modalItem.order.senderName || '-'} · 받는 분 ${
                  bareReceiverName(modalItem.order.receiverName) || '-'
                }`
              : undefined
          }
          target={{ shopOrder: modalItem.shopOrder }}
          onClose={() => setModalItem(null)}
          zIndexClassName="z-50"
        />
      )}
    </div>
  );
};

export default SellerCsMemoLogPage;
